import { Injectable } from "@angular/core";
import { Observable, from, of, timer, race } from "rxjs";
import { catchError, map, take } from "rxjs/operators";

declare const grecaptcha: any;

const TOKEN_TIMEOUT_MS = 10000;

@Injectable({
    providedIn: 'root'
})
export class RecaptchaService {
    private loadPromise: Promise<void> | null = null;
    private loadedKey: string | null = null;

    /** Lazily loads Google's reCAPTCHA v3 script (once). Rejects if the script fails to load. */
    load(siteKey: string): Promise<void> {
        if (this.loadPromise && this.loadedKey === siteKey) {
            return this.loadPromise;
        }
        this.loadedKey = siteKey;
        this.loadPromise = new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://www.google.com/recaptcha/api.js?render=' + encodeURIComponent(siteKey);
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => {
                script.remove();
                this.loadPromise = null; // allow a retry on the next call
                reject(new Error('reCAPTCHA script failed to load'));
            };
            document.head.appendChild(script);
        });
        return this.loadPromise;
    }

    /** Emits a fresh reCAPTCHA v3 token, or null on any failure / after 10 seconds. Never errors. */
    getToken(siteKey: string, action: string): Observable<string | null> {
        const token$ = from(
            this.load(siteKey).then(() => new Promise<string | null>((resolve, reject) => {
                if (typeof grecaptcha === 'undefined' || !grecaptcha) {
                    reject(new Error('grecaptcha is not available'));
                    return;
                }
                grecaptcha.ready(() => {
                    grecaptcha.execute(siteKey, { action }).then(
                        (token: string) => resolve(token || null),
                        (err: unknown) => reject(err));
                });
            }))
        ).pipe(
            catchError(err => {
                console.warn('reCAPTCHA token unavailable', err);
                return of(null);
            })
        );

        const timeout$ = timer(TOKEN_TIMEOUT_MS).pipe(
            map(() => {
                console.warn('reCAPTCHA token timed out');
                return null;
            })
        );

        return race(token$, timeout$).pipe(take(1));
    }
}
